import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import { useAuth } from '../../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { toStorySlug } from '../../../../lib/slugify';

interface Story {
  id: string;
  title: string;
  author_id: string;
  genre: string;
  cover_image: string;
  author?: {
    display_name: string;
  };
  last_read_at?: string;
}

export default function RecentlyReadTab() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentlyRead = async () => {
      if (!user?.id) return;

      try {
        const { data: historyData, error: historyError } = await supabase
          .from('reading_history')
          .select('last_read_at, story_id')
          .eq('user_id', user.id)
          .order('last_read_at', { ascending: false })
          .limit(20);

        if (historyError) throw historyError;

        if (!historyData || historyData.length === 0) {
          setStories([]);
          setLoading(false);
          return;
        }

        const storyIds = historyData.map((item) => item.story_id);
        const { data: storiesData, error: storiesError } = await supabase
          .from('stories')
          .select('id, title, author_id, genre, cover_image')
          .in('id', storyIds);

        if (storiesError) throw storiesError;

        const authorIds = [...new Set(storiesData?.map((s) => s.author_id) || [])];
        let profileMap: Record<string, string> = {};

        if (authorIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, display_name')
            .in('id', authorIds);

          (profiles || []).forEach((p) => {
            profileMap[p.id] = p.display_name;
          });
        }

        const formattedStories: Story[] = historyData
          .map((historyItem) => {
            const storyData = storiesData?.find((s) => s.id === historyItem.story_id);
            if (!storyData) return null;

            return {
              id: storyData.id,
              title: storyData.title,
              author_id: storyData.author_id,
              genre: storyData.genre,
              cover_image: storyData.cover_image,
              author: { display_name: profileMap[storyData.author_id] || 'Unknown' },
              last_read_at: historyItem.last_read_at,
            };
          })
          .filter((item): item is Story => item !== null);

        setStories(formattedStories);
      } catch (error) {
        console.error('Error fetching recently read stories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentlyRead();
  }, [user?.id]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleStoryClick = (story: Story) => {
    const slug = toStorySlug(story.title, story.id);
    navigate(`/story/${slug}`);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-zinc-950 border border-red-900/30 rounded-lg p-5 animate-pulse">
            <div className="flex gap-5">
              <div className="w-24 h-32 bg-zinc-900 rounded flex-shrink-0"></div>
              <div className="flex-1">
                <div className="h-6 bg-zinc-900 rounded mb-2"></div>
                <div className="h-4 bg-zinc-900 rounded w-1/3 mb-3"></div>
                <div className="h-6 bg-zinc-900 rounded w-20"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (stories.length === 0) {
    return (
      <div className="text-center py-16">
        <i className="ri-book-open-line text-6xl text-zinc-700 mb-4"></i>
        <h3 className="text-xl font-bold text-zinc-400 mb-2">No reading history yet</h3>
        <p className="text-zinc-500">Stories you read will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {stories.map((story) => (
        <div
          key={story.id}
          onClick={() => handleStoryClick(story)}
          className="bg-zinc-950 border border-red-900/30 rounded-lg p-5 hover:border-red-900/60 transition-colors cursor-pointer group"
        >
          <div className="flex gap-5">
            <div className="w-24 h-32 flex items-center justify-center flex-shrink-0 overflow-hidden rounded">
              {story.cover_image ? (
                <img
                  src={story.cover_image}
                  alt={story.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              ) : (
                <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                  <i className="ri-book-2-line text-zinc-600 text-3xl"></i>
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-bold text-red-50 mb-2 font-serif group-hover:text-red-400 transition-colors">
                {story.title}
              </h3>

              <p className="text-zinc-400 text-sm mb-3">by {story.author?.display_name || 'Unknown'}</p>

              <div className="flex flex-wrap items-center gap-4">
                {story.genre && (
                  <span className="px-3 py-1 bg-red-900/20 border border-red-900/50 rounded-full text-red-400 text-xs font-medium">
                    {story.genre}
                  </span>
                )}

                {story.last_read_at && (
                  <div className="flex items-center gap-2 text-zinc-500 text-xs">
                    <i className="ri-calendar-line"></i>
                    {formatDate(story.last_read_at)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
